/**
 * TasksScreen Component
 * Shows available tasks and daily streak reward table
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserContext } from '../contexts';
import { useTelegram } from '../hooks/useTelegram';
import { api } from '../services/api';
import type { TaskWithCompletion } from '@trading-sim/shared';

const STREAK_REWARDS = [50, 70, 100, 150, 200, 300, 500];

export function TasksScreen() {
  const { t } = useTranslation();
  const { updateBalance } = useUserContext();
  const { openLink, openTelegramLink, showAlert } = useTelegram();
  const [tasks, setTasks] = useState<TaskWithCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [completingTask, setCompletingTask] = useState<string | null>(null);
  const [dailyStatus, setDailyStatus] = useState<any>(null);
  const [claimingDaily, setClaimingDaily] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchDailyStatus();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.getTasks();
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyStatus = async () => {
    try {
      const res = await api.getDailyRewardStatus();
      setDailyStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch daily status:', error);
    }
  };

  const handleTaskClick = async (task: TaskWithCompletion) => {
    if (task.isCompleted) return;

    try {
      // 1. Immediately open the link (no confirmation popup)
      if (task.url.includes('t.me')) {
        openTelegramLink(task.url);
      } else {
        openLink(task.url);
      }

      // 2. Wait a moment for user to navigate, then complete task
      setCompletingTask(task.id);
      
      // Small delay to ensure link opens first
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const response = await api.completeTask(task.id);
      updateBalance(response.data.newBalance);
      
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, isCompleted: true, completedAt: new Date() } : t
      ));

      showAlert(`✅ ${t('tasks.task_completed').replace('{reward}', task.reward.toString())}`);
    } catch (error: any) {
      const message = error.response?.data?.error?.message || t('tasks.task_error');
      showAlert(`❌ ${message}`);
    } finally {
      setCompletingTask(null);
    }
  };

  const handleClaimDaily = async () => {
    setClaimingDaily(true);
    try {
      const res = await api.claimDailyReward();
      updateBalance(res.data.newBalance);
      showAlert(t('tasks.daily_claimed_alert', { reward: res.data.reward, streak: res.data.newStreak }));
      await fetchDailyStatus();
    } catch (error: any) {
      showAlert(`❌ ${error.response?.data?.error?.message || 'Ошибка'}`);
    } finally {
      setClaimingDaily(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{ padding: '20px', textAlign: 'center', background: 'rgba(10, 22, 40, 0.8)', backdropFilter: 'blur(10px)' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>{t('tasks.title')}</h1>
        <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)', margin: '8px 0 0' }}>{t('tasks.subtitle')}</p>
      </div>

      {/* Daily Streak Reward Table */}
      {dailyStatus && (
        <div style={{ padding: '16px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            borderRadius: '16px', padding: '16px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
              🔥 {t('tasks.daily_reward_title', { streak: dailyStatus.currentStreak })}
            </div>
            
            {/* 7-day grid */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {STREAK_REWARDS.map((reward, i) => {
                const day = i + 1;
                const isClaimed = i < dailyStatus.currentStreak;
                const isNext = i === (dailyStatus.currentStreak % 7);
                return (
                  <div key={day} style={{
                    flex: 1, textAlign: 'center', padding: '8px 2px',
                    background: isClaimed ? 'rgba(16, 185, 129, 0.3)' : isNext ? 'rgba(245, 158, 11, 0.3)' : 'rgba(0,0,0,0.3)',
                    borderRadius: '8px',
                    border: isNext ? '2px solid #f59e0b' : isClaimed ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.1)',
                  }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>{t('tasks.daily_day', { day })}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: isClaimed ? '#10b981' : '#fbbf24' }}>
                      {isClaimed ? '✅' : `+${reward}`}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Claim button */}
            <button
              onClick={handleClaimDaily}
              disabled={!dailyStatus.canClaim || claimingDaily}
              style={{
                width: '100%', padding: '12px',
                background: !dailyStatus.canClaim ? 'rgba(255,255,255,0.15)' : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                border: 'none', borderRadius: '10px', color: '#fff',
                fontSize: '15px', fontWeight: 700,
                cursor: !dailyStatus.canClaim ? 'not-allowed' : 'pointer',
                opacity: !dailyStatus.canClaim ? 0.6 : 1,
              }}
            >
              {!dailyStatus.canClaim ? t('tasks.daily_claimed_today') : claimingDaily ? t('tasks.daily_claiming') : t('tasks.daily_claim_button', { amount: dailyStatus.nextReward })}
            </button>
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div style={{ padding: '0 16px 16px' }}>
        {tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <div>{t('tasks.no_tasks')}</div>
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task.id} style={{
              background: 'rgba(10, 22, 40, 0.6)', borderRadius: '16px', padding: '20px', marginBottom: '12px',
              border: task.isCompleted ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                {/* Icon */}
                <div style={{
                  width: '56px', height: '56px', borderRadius: '12px',
                  background: task.isCompleted ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : (task.icon?.startsWith('/uploads/') ? 'transparent' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', flexShrink: 0,
                  border: task.icon?.startsWith('/uploads/') && !task.isCompleted ? '1px solid rgba(59, 130, 246, 0.3)' : 'none',
                }}>
                  {task.isCompleted ? '✅' : (
                    task.icon?.startsWith('/uploads/') ? (
                      <img src={`https://wintrade.cfd${task.icon}`} alt="Task icon" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }} />
                    ) : (task.icon || '📋')
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: '#fff' }}>{task.title}</h3>
                    <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', background: 'rgba(255, 255, 255, 0.1)', padding: '2px 8px', borderRadius: '12px' }}>{t('tasks.task_label')}</span>
                  </div>
                  <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 12px', lineHeight: '1.4' }}>{task.description}</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#fbbf24' }}>+{task.reward} WiTon</span>
                    {task.isCompleted ? (
                      <div style={{ padding: '8px 16px', background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}>{t('tasks.completed')}</div>
                    ) : (
                      <button onClick={() => handleTaskClick(task)} disabled={completingTask === task.id} style={{
                        padding: '8px 16px',
                        background: completingTask === task.id ? 'rgba(59, 130, 246, 0.5)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                        cursor: completingTask === task.id ? 'not-allowed' : 'pointer',
                      }}>
                        {completingTask === task.id ? t('common.loading') : t('tasks.start')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
